// 围棋通用工具函数 - 供 store 和 AI 共同使用
import type { ChessType, ChessPosition, GameState } from "@/store/game"

export const BOARD_SIZE = 9
export type Stone = ChessType

// 检查位置是否有效
export const isValidPosition = (row: number, col: number): boolean => {
    return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE
}

// 获取相邻位置（返回对象数组，用于store）
export const getNeighborsAsPositions = (
    row: number,
    col: number
): ChessPosition[] => {
    return [
        { row: row - 1, col },
        { row: row + 1, col },
        { row, col: col - 1 },
        { row, col: col + 1 },
    ].filter((pos) => isValidPosition(pos.row, pos.col))
}

// 获取相邻位置（返回元组数组，用于AI / 复杂规则计算）
export const getNeighborsAsTuples = (
    row: number,
    col: number
): Array<[number, number]> => {
    return [
        [row - 1, col],
        [row + 1, col],
        [row, col - 1],
        [row, col + 1],
    ].filter(([r, c]) => isValidPosition(r, c)) as Array<[number, number]>
}

// 获取一组相连的同色棋子
export const getGroup = (
    board: Stone[][],
    row: number,
    col: number,
    stone: Stone
): Set<string> => {
    if (stone === null) return new Set()

    const group = new Set<string>()
    const stack: Array<[number, number]> = [[row, col]]
    const visited = new Set<string>()

    while (stack.length > 0) {
        const [r, c] = stack.pop()!
        const key = `${r},${c}`

        if (visited.has(key)) continue
        visited.add(key)

        if (board[r][c] === stone) {
            group.add(key)

            const neighbors = getNeighborsAsTuples(r, c)
            for (const [nr, nc] of neighbors) {
                if (board[nr][nc] === stone) {
                    stack.push([nr, nc])
                }
            }
        }
    }

    return group
}

// 检查一组棋子是否有气（基于位置的递归版本，用于store）
export const hasLibertyByPosition = (
    board: Stone[][],
    row: number,
    col: number,
    visited: Set<string>
): boolean => {
    const key = `${row}-${col}`
    if (visited.has(key)) return false
    visited.add(key)

    const stone = board[row][col]
    if (!stone) return false

    const neighbors = getNeighborsAsPositions(row, col)

    for (const neighbor of neighbors) {
        const neighborStone = board[neighbor.row][neighbor.col]

        // 如果是空位，说明有气
        if (!neighborStone) {
            return true
        }

        // 如果是同色棋子，继续检查
        if (neighborStone === stone) {
            if (
                hasLibertyByPosition(board, neighbor.row, neighbor.col, visited)
            ) {
                return true
            }
        }
    }

    return false
}

// 移除一组棋子（基于位置的递归版本，用于store）
export const removeGroupByPosition = (
    board: Stone[][],
    row: number,
    col: number,
    stone: Stone
): number => {
    if (!isValidPosition(row, col) || board[row][col] !== stone) {
        return 0
    }

    let count = 0
    const stack: ChessPosition[] = [{ row, col }]
    const visited = new Set<string>()

    while (stack.length > 0) {
        const current = stack.pop()!
        const key = `${current.row}-${current.col}`

        if (visited.has(key)) continue
        visited.add(key)

        if (board[current.row][current.col] === stone) {
            board[current.row][current.col] = null
            count++

            const neighbors = getNeighborsAsPositions(current.row, current.col)
            for (const neighbor of neighbors) {
                if (board[neighbor.row][neighbor.col] === stone) {
                    stack.push(neighbor)
                }
            }
        }
    }

    return count
}

// 检查并提子（基于位置的版本，用于store）
// 返回被吃的数量和被吃的位置（用于打劫规则）
export const captureOpponentPieces = (
    board: Stone[][],
    row: number,
    col: number,
    currentPlayer: boolean
): { count: number; capturedPositions: ChessPosition[] } => {
    const opponentStone: Stone = currentPlayer ? "white" : "black"

    let totalCaptured = 0
    const capturedPositions: ChessPosition[] = []
    const neighbors = getNeighborsAsPositions(row, col)

    for (const neighbor of neighbors) {
        const neighborStone = board[neighbor.row][neighbor.col]

        if (neighborStone === opponentStone) {
            const visited = new Set<string>()
            if (
                !hasLibertyByPosition(
                    board,
                    neighbor.row,
                    neighbor.col,
                    visited
                )
            ) {
                // 记录被吃的位置（在提子前记录）
                capturedPositions.push({ row: neighbor.row, col: neighbor.col })
                const captured = removeGroupByPosition(
                    board,
                    neighbor.row,
                    neighbor.col,
                    opponentStone
                )
                totalCaptured += captured
            }
        }
    }

    return { count: totalCaptured, capturedPositions }
}

// ================== Store 专用落子合法性工具 ==================

// 检查位置是否可以落子（包括边界、已有棋子、打劫、自杀规则）
// 注意：这里的实现是给 store / MainGame 使用的，不直接耦合 Zustand，只依赖传入的参数
export const isValidMoveForStore = (
    board: Stone[][],
    row: number,
    col: number,
    currentPlayer: boolean,
    koPosition: ChessPosition | null
): boolean => {
    // 检查边界
    if (!isValidPosition(row, col)) {
        return false
    }

    // 检查是否已有棋子
    if (board[row][col]) {
        return false
    }

    // 检查是否是打劫禁入点
    if (koPosition && koPosition.row === row && koPosition.col === col) {
        return false
    }

    // 检查自杀规则：模拟落子，检查是否会导致自杀
    const testBoard = board.map((r) => [...r])
    const playerColor: Stone = currentPlayer ? "black" : "white"
    testBoard[row][col] = playerColor

    // 先提对方的子
    const captureResult = captureOpponentPieces(
        testBoard,
        row,
        col,
        currentPlayer
    )
    const captured = captureResult.count

    // 检查自己的棋子是否有气
    const visited = new Set<string>()
    const hasLiberty = hasLibertyByPosition(testBoard, row, col, visited)

    // 如果没吃子且自己没气，这是自杀
    if (captured === 0 && !hasLiberty) {
        return false
    }

    return true
}

// 根据当前棋盘状态获取所有合法落子位置（给 store / MainGame 使用）
export const getAllValidMovesFromState = (
    board: Stone[][],
    currentPlayer: boolean,
    koPosition: ChessPosition | null
): ChessPosition[] => {
    const validMoves: ChessPosition[] = []

    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            if (
                isValidMoveForStore(board, row, col, currentPlayer, koPosition)
            ) {
                validMoves.push({ row, col })
            }
        }
    }

    return validMoves
}

// 创建空棋盘
export const createEmptyBoard = (size: number): Stone[][] => {
    return Array(size)
        .fill(null)
        .map(() => Array(size).fill(null))
}

// ========== AI专用部分 ==========

// AI专用的GameState接口（扩展store的GameState）
export interface AIGameState {
    board: Stone[][]
    currentPlayer: "black" | "white"
    moveHistory: ChessPosition[]
    capturedBlack: number
    capturedWhite: number
    koPosition: ChessPosition | null // 打劫禁入点
}

// 从store状态转换为AI状态
export const convertToAIState = (storeState: GameState): AIGameState => {
    return {
        board: storeState.board as Stone[][],
        currentPlayer: storeState.currentPlayer ? "black" : "white",
        moveHistory: storeState.history,
        capturedBlack: storeState.whiteCaptures, // 黑棋吃的白子数
        capturedWhite: storeState.blackCaptures, // 白棋吃的黑子数
        koPosition: storeState.koPosition, // 传递打劫禁入点
    }
}
